import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { triangulate } from '../../geo/triangulate';
import type { ArchBridgeSpec, Station } from './archBridge';
import { upright, uprightStrip, type FlatTriangle } from './uprightStrip';

/** What every face of one bridge is built with. */
export interface ArchFaces {
  readonly builder: MeshBuilder;
  readonly style: VertexStyle;
  readonly frame: Frame;
  readonly spec: ArchBridgeSpec;
}

function topOf(spec: ArchBridgeSpec, x: number): number {
  return spec.deckY(x) + spec.parapet.height;
}

// An upright edge of a slab from the underside to the parapet top. Where the underside steps up at
// the station on this slab's side, the jamb's top is on the edge too, so the slab meets the jamb
// and the next slab at shared corners.
function uprightAt(spec: ArchBridgeSpec, x: number, low: number, high: number): Vec2[] {
  return upright(x, [...(high > low ? [low, high] : [low]), topOf(spec, x)]);
}

/** The side face between two stations, counter-clockwise in x and y. */
export function slabTriangles(spec: ArchBridgeSpec, a: Station, b: Station): FlatTriangle[] {
  return uprightStrip(
    uprightAt(spec, a.x, a.after, a.before),
    uprightAt(spec, b.x, b.before, b.after)
  );
}

function buildSides(faces: ArchFaces, stations: readonly Station[]): void {
  const { builder, style, frame, spec } = faces;
  const half = spec.width / 2;
  const at = (p: Vec2, z: number): Vec3 => [p[0], p[1], z];
  const uv = (p: Vec3): Vec2 => [p[0], p[1]];
  stations.slice(0, -1).forEach((a, i) => {
    for (const [p, q, r] of slabTriangles(spec, a, stations[i + 1]!)) {
      builder.polygon(style, frame, [at(p, half), at(q, half), at(r, half)], uv);
      builder.polygon(style, frame, [at(r, -half), at(q, -half), at(p, -half)], uv);
    }
  });
}

// Each underside edge runs with the solid on its left, so its strip across the width faces out of it.
function buildUnderside(faces: ArchFaces, chain: readonly Vec2[]): void {
  const { builder, style, frame, spec } = faces;
  const half = spec.width / 2;
  chain.slice(0, -1).reduce((along, p, i) => {
    const q = chain[i + 1]!;
    const next = along + Math.hypot(q[0] - p[0], q[1] - p[1]);
    builder.quad(
      style,
      frame,
      [
        [p[0], p[1], -half],
        [q[0], q[1], -half],
        [q[0], q[1], half],
        [p[0], p[1], half],
      ],
      [
        [along, -half],
        [next, -half],
        [next, half],
        [along, half],
      ]
    );
    return next;
  }, 0);
}

// A strip between two stations at heights `ya` and `yb`, from `z0` to `z1` across, facing up.
function strip(
  faces: ArchFaces,
  [a, b]: readonly [number, number],
  [ya, yb]: readonly [number, number],
  [z0, z1]: readonly [number, number]
): void {
  faces.builder.quad(
    faces.style,
    faces.frame,
    [
      [a, ya, z1],
      [b, yb, z1],
      [b, yb, z0],
      [a, ya, z0],
    ],
    [
      [a, z1],
      [b, z1],
      [b, z0],
      [a, z0],
    ]
  );
}

// The inner face of a parapet, facing the walkway: `side` is +1 on the right and -1 on the left.
function parapetFace(faces: ArchFaces, [a, b]: readonly [number, number], side: 1 | -1): void {
  const { builder, style, frame, spec } = faces;
  const z = side * (spec.width / 2 - spec.parapet.thickness);
  const corners: [Vec3, Vec3, Vec3, Vec3] = [
    [a, spec.deckY(a), z],
    [a, topOf(spec, a), z],
    [b, topOf(spec, b), z],
    [b, spec.deckY(b), z],
  ];
  const uv = corners.map((p): Vec2 => [p[0], p[1]]) as [Vec2, Vec2, Vec2, Vec2];
  if (side === 1) builder.quad(style, frame, corners, uv);
  else {
    builder.quad(
      style,
      frame,
      [corners[0], corners[3], corners[2], corners[1]],
      [uv[0], uv[3], uv[2], uv[1]]
    );
  }
}

// The walkway, both copings and both parapets' inner faces break at every station the sides break
// at, so the top and the sides share every corner along the coping edges.
function buildTop(faces: ArchFaces, xs: readonly number[]): void {
  const { spec } = faces;
  const half = spec.width / 2;
  const inner = half - spec.parapet.thickness;
  xs.slice(0, -1).forEach((a, i) => {
    const pair = [a, xs[i + 1]!] as const;
    if (spec.parapet.height === 0) {
      strip(faces, pair, [spec.deckY(pair[0]), spec.deckY(pair[1])], [-half, half]);
      return;
    }
    const cap = [topOf(spec, pair[0]), topOf(spec, pair[1])] as const;
    strip(faces, pair, [spec.deckY(pair[0]), spec.deckY(pair[1])], [-inner, inner]);
    strip(faces, pair, cap, [inner, half]);
    strip(faces, pair, cap, [-half, -inner]);
    parapetFace(faces, pair, 1);
    parapetFace(faces, pair, -1);
  });
}

function signedArea(a: Vec2, b: Vec2, c: Vec2): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1]);
}

// Each triangle of a flat outline, wound counter-clockwise in its own plane.
function flatTriangles(outline: readonly Vec2[]): FlatTriangle[] {
  const { positions, indices } = triangulate({ outer: [...outline], holes: [] });
  const point = (v: number): Vec2 => [positions[2 * v]!, positions[2 * v + 1]!];
  return Array.from({ length: indices.length / 3 }, (_, t) => {
    const [a, b, c] = [0, 1, 2].map((k) => point(indices[3 * t + k]!)) as [Vec2, Vec2, Vec2];
    return signedArea(a, b, c) >= 0 ? ([a, b, c] as const) : ([a, c, b] as const);
  });
}

// The end faces, U-shaped round the walkway: at x = 0 facing back along the axis, at the far end ahead.
function buildEnds(faces: ArchFaces): void {
  const { builder, style, frame, spec } = faces;
  const half = spec.width / 2;
  const inner = half - spec.parapet.thickness;
  for (const x of [0, spec.length]) {
    const [deck, cap] = [spec.deckY(x), topOf(spec, x)];
    const outline: Vec2[] =
      spec.parapet.height === 0
        ? [
            [-half, spec.footY],
            [half, spec.footY],
            [half, deck],
            [-half, deck],
          ]
        : [
            [-half, spec.footY],
            [half, spec.footY],
            [half, cap],
            [inner, cap],
            [inner, deck],
            [-inner, deck],
            [-inner, cap],
            [-half, cap],
          ];
    const at = (p: Vec2): Vec3 => [x, p[1], p[0]];
    const uv = (p: Vec3): Vec2 => [p[2], p[1]];
    for (const [a, b, c] of flatTriangles(outline)) {
      const wound = x === 0 ? [a, b, c] : [c, b, a];
      builder.polygon(style, frame, wound.map(at), uv);
    }
  }
}

/** Every face of the bridge, from its stations and the underside chain through them. */
export function buildArchFaces(
  faces: ArchFaces,
  stations: readonly Station[],
  chain: readonly Vec2[]
): void {
  buildSides(faces, stations);
  buildUnderside(faces, chain);
  if (faces.spec.topFaces !== false) {
    buildTop(faces, stations.map((s) => s.x));
  }
  if (faces.spec.endFaces !== false) buildEnds(faces);
}
